import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { FeedbackEvent } from '@bonusly/common-feedback';
import {
	ArchiveSolid,
	Box,
	ChatAltSolid,
	EmojiHappySolid,
	Flex,
	HStack,
	IconButton,
	MegaphoneSolid,
	Modal,
	ModalContent,
	ModalOverlay,
	PencilSolid,
	PlusOutline,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	rem,
	SearchOutline,
	ShoppingCartOutline,
	Text,
	TrophySolid,
	useBreakpointValue,
	useDisclosure,
	useOutsideClick,
	VideoCameraSolid,
	VStack,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { Permission, ProtectedContent, ProtectedContentWithPermissionOrFeatureFlag } from '@bonusly/who-am-i';

import { useAwardData } from '../../modules/bonus-feed/features/recognition/components/Awards/hooks/useAwardData';
import { useAwardModal } from '../../modules/bonus-feed/features/recognition/components/Awards/hooks/useAwardModal';
import { useCheckInModal } from '../../modules/check-ins/hooks/use-checkin-modal';
import { useNominationModal } from '../../modules/nominations/hooks';
import { useCheckInsConfigured } from '../../modules/team-management/hooks/use-check-ins-configured';
import { useNominationsCount } from '../../queries/nominations';
import { VideoRecordingEvent } from '../action-input/events';
import { useMakeAnnouncementModal } from '../announcements/hooks';
import { GiveBoxEvent } from '../recognition/give-box/events';
import { SearchContent } from '../search';

export const MobileActionBar: FC = () => {
	const { t } = useTranslation('components');
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const dispatch = useDispatch();
	const awardModal = useAwardModal();
	const navigate = useNavigate();
	const location = useLocation();
	const nominationModal = useNominationModal();
	const { open: nominationsOpen } = useNominationsCount();
	const { onOpen: handleOpenCheckInModal } = useCheckInModal();
	const hasAwardsToGive = useAwardData().shouldDisplayBanner;
	const checkInsEnabled = useCheckInsConfigured();

	// Modal disclosures
	const searchModalDisclosure = useDisclosure();
	const buttonRef = useRef(null);
	const isAtLeastSm = useBreakpointValue({ base: false, xs: true });
	const { onOpen: openAnnouncementModal } = useMakeAnnouncementModal();
	const popoverDisclosure = useDisclosure();
	const popoverRef = useRef(null);

	// Handle clicks outside the popover to close it without triggering other elements
	useOutsideClick({
		ref: popoverRef,
		handler: (event) => {
			if (popoverDisclosure.isOpen) {
				event.preventDefault();
				event.stopPropagation();
				popoverDisclosure.onClose();
			}
		},
	});

	// Handlers
	const handleWriteRecognition = () => {
		popoverDisclosure.onClose();
		dispatch(GiveBoxEvent.Open);
	};

	const handleRecordRecognition = () => {
		popoverDisclosure.onClose();
		dispatch(GiveBoxEvent.Open);
		dispatch(VideoRecordingEvent.OpenModal, { contextType: 'recognition' });
	};
	const handleFeedback = () => {
		popoverDisclosure.onClose();
		dispatch(FeedbackEvent.OpenRequest);
	};
	const handleAnnounce = () => {
		popoverDisclosure.onClose();
		openAnnouncementModal();
	};
	const handleAward = () => {
		popoverDisclosure.onClose();
		awardModal.onOpen();
	};
	const handleRewards = () => {
		popoverDisclosure.onClose();
		navigate('/rewards');
	};
	const handleNominate = () => {
		popoverDisclosure.onClose();
		nominationModal.onOpen();
	};
	const handleCheckIn = () => {
		popoverDisclosure.onClose();
		handleOpenCheckInModal();
	};
	// Menu options
	const menuOptions = [
		<ProtectedContentWithPermissionOrFeatureFlag key="feedback" flag={['one-on-ones', 'achieve-limited-release']} permissions={Permission.CanAccessOneOnOnes}>
			<HStack as="button" w="full" px={2} py={2} borderRadius="md" _hover={{ bg: 'bg.subtle' }} onClick={handleFeedback}>
				<ChatAltSolid color="purple.500" />
				<Text fontWeight="medium">{t('mobile_action_bar.feedback')}</Text>
			</HStack>
		</ProtectedContentWithPermissionOrFeatureFlag>,
		checkInsEnabled && (
			<ProtectedContentWithPermissionOrFeatureFlag key="checkin" flag={['manager-enablement-check-in']} permissions={[Permission.CanAccessCheckIn]}>
				<HStack as="button" w="full" px={2} py={2} borderRadius="md" _hover={{ bg: 'bg.subtle' }} onClick={handleCheckIn}>
					<EmojiHappySolid color="green.300" />
					<Text fontWeight="medium">{t('mobile_action_bar.check_in')}</Text>
				</HStack>
			</ProtectedContentWithPermissionOrFeatureFlag>
		),
		nominationsOpen > 0 && (
			<ProtectedContent key="nominate" permissions={[Permission.CanAccessNominations]}>
				<HStack as="button" w="full" px={2} py={2} borderRadius="md" _hover={{ bg: 'bg.subtle' }} onClick={handleNominate}>
					<ArchiveSolid color="#199CB3" />
					<Text fontWeight="medium">{t('mobile_action_bar.nominate')}</Text>
				</HStack>
			</ProtectedContent>
		),
		hasAwardsToGive && (
			<ProtectedContent key="award" permissions={[Permission.CanAccessAwards]}>
				<HStack as="button" w="full" px={2} py={2} borderRadius="md" _hover={{ bg: 'bg.subtle' }} onClick={handleAward}>
					<TrophySolid color="yellow.500" />
					<Text fontWeight="medium">{t('mobile_action_bar.award')}</Text>
				</HStack>
			</ProtectedContent>
		),
		<ProtectedContent key="announce" permissions={[Permission.CanAdministerAnnouncements]}>
			<HStack as="button" w="full" px={2} py={2} borderRadius="md" _hover={{ bg: 'bg.subtle' }} onClick={handleAnnounce}>
				<MegaphoneSolid color="pink.500" />
				<Text fontWeight="medium">{t('mobile_action_bar.announce')}</Text>
			</HStack>
		</ProtectedContent>,
		<ProtectedContent key="write-recognition" permissions={[Permission.CanGiveRecognition]}>
			<HStack as="button" w="full" px={2} py={2} borderRadius="md" _hover={{ bg: 'bg.subtle' }} onClick={handleWriteRecognition}>
				<PencilSolid color="blue.500" />
				<Text fontWeight="medium">{t('mobile_action_bar.write_recognition')}</Text>
			</HStack>
		</ProtectedContent>,
		<ProtectedContent key="record-recognition" permissions={[Permission.CanGiveRecognition, Permission.CanRecordVideo]}>
			<HStack as="button" w="full" px={2} py={2} borderRadius="md" _hover={{ bg: 'bg.subtle' }} onClick={handleRecordRecognition}>
				<VideoCameraSolid color="blue.500" />
				<Text fontWeight="medium">{t('mobile_action_bar.record_recognition')}</Text>
			</HStack>
		</ProtectedContent>,
	].filter(Boolean);

	return (
		isMobile &&
		location.pathname === '/' && (
			<Flex
				position="fixed"
				bottom={rem(16)}
				left="50%"
				transform="translateX(-50%)"
				width={rem(200)}
				height={rem(70)}
				bg={'white'}
				borderRadius="999px"
				boxShadow="0 8px 32px rgba(24,119,242,0.18), 0 1.5px 6px rgba(0,0,0,0.10)"
				borderColor={'gray.200'}
				alignItems="center"
				justifyContent="space-between"
				px={3}
				zIndex="docked"
			>
				<IconButton
					aria-label={t('mobile_action_bar.search')}
					icon={<SearchOutline boxSize={7} />}
					variant="ghost"
					size="lg"
					colorScheme="gray"
					minW={rem(44)}
					h={rem(44)}
					borderRadius="full"
					onClick={searchModalDisclosure.onOpen}
					ref={buttonRef}
				/>
				<Modal finalFocusRef={buttonRef} isOpen={searchModalDisclosure.isOpen} onClose={searchModalDisclosure.onClose} scrollBehavior="inside" size="2xl">
					<ModalOverlay />
					<ModalContent
						maxH={`min(calc(100% - ${rem(7.5)}), ${rem(664)})`}
						{...(!isAtLeastSm && {
							height: 'auto',
							maxHeight: '100%',
							borderRadius: 0,
							maxH: 'auto',
							my: 0,
						})}
					>
						<SearchContent onClose={searchModalDisclosure.onClose} />
					</ModalContent>
				</Modal>

				<Box ref={popoverRef}>
					<Popover
						placement="top"
						trigger="click"
						closeOnBlur={true}
						closeOnEsc={true}
						isOpen={popoverDisclosure.isOpen}
						onOpen={popoverDisclosure.onOpen}
						onClose={popoverDisclosure.onClose}
					>
						<PopoverTrigger>
							<IconButton
								aria-label={t('mobile_action_bar.add')}
								icon={<PlusOutline boxSize={9} />}
								variant="solid"
								size="xl"
								color="white"
								bgGradient="linear(to-br, #5bb6ff, #1877f2)"
								borderRadius="full"
								boxShadow="md"
								minW={rem(52)}
								h={rem(52)}
								mx={2}
								_hover={{ bgGradient: 'linear(to-br, #5bb6ff, #1877f2)' }}
								_active={{ bgGradient: 'linear(to-br, #1877f2, #5bb6ff)' }}
							/>
						</PopoverTrigger>

						<PopoverContent borderRadius="xl" boxShadow="xl" w={rem(240)} p={2} border="none" zIndex="popover">
							<PopoverBody>
								<VStack align="stretch" spacing={2}>
									{menuOptions}
								</VStack>
							</PopoverBody>
						</PopoverContent>
					</Popover>
				</Box>

				<IconButton
					aria-label={t('mobile_action_bar.shopping_cart')}
					icon={<ShoppingCartOutline boxSize={7} />}
					variant="ghost"
					size="lg"
					colorScheme="gray"
					minW={rem(44)}
					h={rem(44)}
					borderRadius="full"
					onClick={handleRewards}
				/>
			</Flex>
		)
	);
};
